import { ChangeDetectionStrategy, Component, Input, OnInit } from '@angular/core';
import { Observable } from 'rxjs';

import { lowBatteryThreshold, lowLqiSignalThreshold } from '@common';
import {
  ActiveErrorData,
  ActiveErrorsStore,
  InstallationDetailsStore,
  InstallationFeaturesStore,
} from '@store';

import {
  BatteryProperties,
  DcSensorValueProperties,
  DeviceConnectionStatus,
  DeviceFirmwareVersion,
  DeviceNameCommands,
  DeviceNameProperties,
  Feature,
  FeatureNames,
  FeaturePropertyKey,
  RoomSensorsCo2Properties,
  SignalProperties,
} from '@vi-iot/client';

import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { GTMService } from '@services';
import { SUFFIX } from '../../../shared/common/units';
import { NO_VALUE } from '../../../shared/pipes';
import { InstallationService } from '../../services/installation.service';
import { EditDeviceNameModalComponent } from '../edit-device-name-modal/edit-device-name-modal.component';
@Component({
  selector: 'app-air-quality-sensor-panel',
  templateUrl: './air-quality-sensor-panel.component.html',
  styleUrls: ['./air-quality-sensor-panel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AirQualitySensorPanelComponent implements OnInit {
  readonly SUFFIX = SUFFIX;
  readonly lowLqiSignalThreshold = lowLqiSignalThreshold;
  readonly lowBatteryThreshold = lowBatteryThreshold;
  readonly featurePropertyKey = FeaturePropertyKey;

  featuresLoading$: Observable<boolean>;
  battery$: Observable<Feature<BatteryProperties, never>>;
  wifiLqi$: Observable<Feature<SignalProperties, never>>;
  sensorCo2$: Observable<Feature<RoomSensorsCo2Properties, never>>;
  temperature$: Observable<Feature<DcSensorValueProperties, never>>;
  humidity$: Observable<Feature<DcSensorValueProperties, never>>;
  status$: Observable<Feature<DeviceConnectionStatus, never>>;
  firmware$: Observable<Feature<DeviceFirmwareVersion, never>>;
  installationTimezone$: Observable<string> = this.installationDetailsStore.installationTimezone$;
  deviceErrors$: Observable<ActiveErrorData[]>;
  deviceName$: Observable<Feature<DeviceNameProperties, DeviceNameCommands>>;
  editDeviceNameDialogRef: MatDialogRef<EditDeviceNameModalComponent>;

  noValue = NO_VALUE;

  @Input() deviceId: string;
  @Input() roomId: string;

  constructor(
    public activeErrorsStore: ActiveErrorsStore,
    public installationService: InstallationService,
    private installationDetailsStore: InstallationDetailsStore,
    private dialog: MatDialog,
    private gtmService: GTMService,
    private installationFeaturesStore: InstallationFeaturesStore
  ) {}

  ngOnInit(): void {
    this.featuresLoading$ = this.installationFeaturesStore.featuresLoading$;
    this.sensorCo2$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.deviceSensorsCo2,
      this.deviceId
    );
    this.battery$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.devicePowerBattery,
      this.deviceId
    );
    this.wifiLqi$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.deviceZigbeeLqi,
      this.deviceId
    );
    this.temperature$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.deviceTemperatureSensor,
      this.deviceId
    );
    this.humidity$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.deviceHumiditySensor,
      this.deviceId
    );
    this.status$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.deviceConnection,
      this.deviceId
    );
    this.firmware$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.essFirmwareBundle,
      this.deviceId
    );
    this.deviceErrors$ = this.activeErrorsStore.getActiveErrorsForSRCDevice(this.deviceId);
    this.deviceName$ = this.installationFeaturesStore.selectInstallationDeviceFeature<
      DeviceNameProperties,
      DeviceNameCommands
    >(FeatureNames.deviceName, this.deviceId);
  }
  openEditDeviceNameDialog(deviceId: string): void {
    this.gtmService.track('onOpen', 'Edit Device Name modal', 'singleRoomControl');
    this.editDeviceNameDialogRef = this.dialog.open(EditDeviceNameModalComponent, {
      width: '715px',
      panelClass: 'commissioning-edit-room-dialog',
      data: {
        deviceId,
      },
    });
  }
}
